import { z } from 'zod'
import {
  benefitAndVoucherManagementGetExclusiveBenefitListSchema,
  type BenefitAndVoucherManagementGetExclusiveBenefitList
} from '@/models/api/benefit-and-voucher-management-get-exclusive-benefit-list'
import {
  getPointAndBenefitInformationSchema,
  type GetPointAndBenefitInformation
} from '@/models/api/get-point-and-benefit-information'
import type { ManageBenefitRedeemGetPointListRequest } from '@/models/api/manage-benefit-redeem-get-point-list-request'
import {
  manageBenefitRedeemShoppingCartSchema,
  type ManageBenefitRedeemShoppingCart
} from '@/models/api/manage-benefit-redeem-shopping-cart'
import type { ManageBenefitRedeemShoppingCartRequest } from '@/models/api/manage-benefit-redeem-shopping-cart-request'

import {
  managerBenefitSchema,
  type ManagerBenefit
} from '@/models/api/manager-benefit'
import {
  managerBenefitGetCompanyEmployeeMileageSchema,
  type ManagerBenefitGetCompanyEmployeeMileage
} from '@/models/api/manager-benefit-get-company-employee-mileage'
import {
  managerBenefitGetCorporateMileageSchema,
  type ManagerBenefitGetCorporateMileage
} from '@/models/api/manager-benefit-get-corporate-mileage'
import {
  managerBenefitGetCorporatePointSchema,
  type ManagerBenefitGetCorporatePoint
} from '@/models/api/manager-benefit-get-corporate-point'
import {
  managerBenefitGetDetailSchema,
  type ManagerBenefitGetDetail
} from '@/models/api/manager-benefit-get-detail'
import type { ManagerBenefitGetDetailRequest } from '@/models/api/manager-benefit-get-detail-request'
import type { ManagerBenefitGetListRequest } from '@/models/api/manager-benefit-get-list-request'
import type { ManagerBenefitRedeemMileageRequest } from '@/models/api/manager-benefit-redeem-mileage-request'
import type { ManagerBenefitReneShoppingCartRequest } from '@/models/api/manager-benefit-renew-shopping-cart-request'
import {
  pointHistoryListDataSchema,
  pointInfoSchema,
  type PointHistoryListData,
  type PointInfo
} from '~library/models'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class ManagerBenefitApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得禮遇項目清單
   * https://hackmd.io/aUnIPGS-SsmWRDOwisDY-Q
   */
  @apiValidator(managerBenefitSchema.array())
  managerBenefitedList(
    query: ManagerBenefitGetListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ManagerBenefit[]>> {
    return this.api.post('ManagerBenefit/GetList', query, config)
  }

  /**
   * API 兌換購物車中的禮遇項目
   * https://hackmd.io/ro71KsnXRsaLpeGT5WVEPQ
   */
  @apiValidator(manageBenefitRedeemShoppingCartSchema.array())
  redeemShoppingCart(
    query: ManageBenefitRedeemShoppingCartRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ManageBenefitRedeemShoppingCart[]>> {
    return this.api.post('ManagerBenefit/RedeemShoppingCart', query, config)
  }

  /**
   * API 取得點數明細查詢清單
   * https://hackmd.io/IyVmXcrqQVmKABpGzYQk2Q
   */
  @apiValidator(pointInfoSchema)
  getPointList(
    query: ManageBenefitRedeemGetPointListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<PointInfo>> {
    return this.api.post('ManagerBenefit/GetPointList', query, config)
  }

  /**
   * API 取得點數編號之歷程資料
   * https://hackmd.io/afdUEebaQyylclINrxI1EA
   */
  @apiValidator(pointHistoryListDataSchema)
  getPointHistory(
    pointNo: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<PointHistoryListData>> {
    return this.api.get(`ManagerBenefit/GetPointHistory/${pointNo}`, config)
  }

  /**
   * API 取得客戶點數及近三筆即將到期點數資料
   * https://hackmd.io/C2X0og4jTaS5-cLprFburw
   */
  @apiValidator(managerBenefitGetCorporatePointSchema)
  getCorporatePoint(): Promise<ApiResponse<ManagerBenefitGetCorporatePoint>> {
    return this.api.get('ManagerBenefit/GetCorporatePoint')
  }

  /**
   * API 取得禮遇項目資料
   * https://hackmd.io/UF0IFa1KR8uqnbEyDkNz0g
   */
  @apiValidator(managerBenefitGetDetailSchema)
  managerBenefitGetDetail(
    query: ManagerBenefitGetDetailRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ManagerBenefitGetDetail>> {
    return this.api.post('ManagerBenefit/GetDetail', query, config)
  }

  /**
   * API 更新購物車商品數量
   * https://hackmd.io/3v3IBgseSyqpgYkcUOycig
   */
  @apiValidator(z.null())
  renewShoppingCartUpdateData(
    data: ManagerBenefitReneShoppingCartRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<null>> {
    return this.api.updateData('ManagerBenefit/RenewShoppingCart', data, config)
  }

  /**
   * API 使用點數兌換哩程
   * https://hackmd.io/smac_GFQRfaumde8p0jLOw
   */
  redeemMileage(
    query: ManagerBenefitRedeemMileageRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.post('ManagerBenefit/RedeemMileage', query, config)
  }

  /**
   * API 取得客戶已兌換哩程數
   * https://hackmd.io/xC9dEbuBSL6vDQEAW8d2GQ
   */
  @apiValidator(managerBenefitGetCorporateMileageSchema)
  getCorporateMileageBizIdGetData(
    bizId: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ManagerBenefitGetCorporateMileage>> {
    return this.api.getData(
      `ManagerBenefit/GetCorporateMileage/${bizId}`,
      config
    )
  }

  /**
   * API 取得企業員工已兌換哩程數
   * https://hackmd.io/C-LVspW6S9iJzRf28gP7Bw
   */
  @apiValidator(managerBenefitGetCompanyEmployeeMileageSchema)
  getCompanyEmployeeMileage(
    memberCardNumber: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<ManagerBenefitGetCompanyEmployeeMileage>> {
    return this.api.get(
      `ManagerBenefit/GetCompanyEmployeeMileage/${memberCardNumber}`,
      config
    )
  }

  /**
   * API 取得專屬禮遇清單
   * https://hackmd.io/1imJ1nCISlOV_SQR-ow5FQ
   */
  @apiValidator(
    benefitAndVoucherManagementGetExclusiveBenefitListSchema.array()
  )
  getExclusiveBenefitList(
    memberCardNumber: string,
    config?: ApiRequestConfig
  ): Promise<
    ApiResponse<BenefitAndVoucherManagementGetExclusiveBenefitList[]>
  > {
    return this.api.get(
      `ManagerBenefit/GetExclusiveBenefitList/${memberCardNumber}`,
      config
    )
  }

  /**
   * API 取得點數與禮遇資訊
   * https://hackmd.io/nPmKICCGRpKiYJFW1OysQA
   */
  @apiValidator(getPointAndBenefitInformationSchema)
  getPointAndBenefitInformation(): Promise<
    ApiResponse<GetPointAndBenefitInformation>
  > {
    return this.api.get('/ManagerBenefit/GetPointAndBenefitInformation')
  }
}
